import React, { useEffect } from 'react';
import { X } from 'lucide-react';

// This is a fixed modal component that doesn't use animations
const FixedModal = ({ isOpen, onClose, title, children }) => {
  // Use useEffect to prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save original body style
      const originalStyle = window.getComputedStyle(document.body).overflow;
      // Disable scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
      
      // For iOS Safari - prevent header from overlapping modal
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const documentHeight = document.documentElement.clientHeight;
      
      // If there's a significant difference, apply a specific iOS fix
      if (Math.abs(viewportHeight - documentHeight) > 50) {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
      }
      
      // Re-enable scrolling when component unmounts or modal closes
      return () => {
        document.body.style.overflow = originalStyle;
        
        // Reset iOS-specific fixes
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
      };
    }
  }, [isOpen]);
  
  // Return empty div instead of null when closed to maintain the component instance
  if (!isOpen) return <div className="hidden"></div>;
  
  return (
    <div 
      className="fixed inset-0 overflow-y-auto bg-black/80 backdrop-blur-sm py-6 z-[9999]" 
      style={{
        // Force hardware acceleration to ensure proper stacking
        transform: 'translateZ(0)',
        // Ensure modal is above everything on iOS
        WebkitTransform: 'translateZ(0)',
        // iOS specific properties
        WebkitOverflowScrolling: 'touch',
        // Ensure the modal container extends beyond the bottom of Safari's UI
        minHeight: '100vh',
        minHeight: '-webkit-fill-available',
        // Improved positioning for iOS Safari
        top: '0',
        bottom: '0',
        left: '0',
        right: '0'
      }}
      onClick={onClose}
    >
      <div className="min-h-[calc(100dvh-3rem)] flex items-center justify-center">
        <div 
          className="w-full max-w-2xl mx-auto bg-gradient-to-b from-slate-950 to-black md:rounded-xl overflow-hidden shadow-2xl border border-red-900/30 relative my-6 max-sm:mt-10" 
          onClick={e => e.stopPropagation()}
          style={{
            // Ensure modal content has proper stacking context
            transform: 'translateZ(0)',
            WebkitTransform: 'translateZ(0)',
            // Fix for iOS Safari
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {title && (
            <div className="bg-gradient-to-r from-red-800 via-red-900 to-red-950 p-4 relative z-10">
              <h2 className="text-2xl font-bold text-white text-center">{title}</h2>
            </div>
          )}
          <button className="absolute top-3 right-3 p-2 text-white/80 hover:text-white hover:bg-red-900/20 rounded-full transition-all z-20" onClick={onClose}>
            <X size={20} />
          </button>
          <div className="relative">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FixedModal; 